import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Palette, 
    Database, 
    Code, 
    Globe, 
    DollarSign, 
    Lock, 
    Crown,
    ChevronRight,
    Loader2,
    X,
    CheckCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OPTION_ICONS = {
    ui_design: Palette,
    database: Database,
    code_starter: Code,
    api: Globe,
    monetization: DollarSign
};

const OPTION_COLORS = {
    ui_design: 'text-pink-400 bg-pink-500/20',
    database: 'text-blue-400 bg-blue-500/20',
    code_starter: 'text-green-400 bg-green-500/20',
    api: 'text-orange-400 bg-orange-500/20',
    monetization: 'text-yellow-400 bg-yellow-500/20'
};

export const ContinueBuilding = ({ 
    blueprintId,
    originalIdea,
    blueprintSummary,
    projectId,  // Optional: if provided, auto-save to project
    isPro = false,
    onUpgrade,
    onContentGenerated  // Callback when new content is generated
}) => {
    const { isAuthenticated, getAuthHeaders } = useAuth();
    const [selectedOption, setSelectedOption] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [result, setResult] = useState(null);
    const [showResult, setShowResult] = useState(false);

    const OPTIONS = {
        ui_design: {
            name: "UI Design",
            description: "Wireframes & components"
        },
        database: {
            name: "Database Schema",
            description: "Tables & relationships"
        },
        code_starter: {
            name: "Code Starter",
            description: "Project scaffolding"
        },
        api: {
            name: "API Design",
            description: "Endpoints & contracts"
        },
        monetization: {
            name: "Monetization",
            description: "Revenue strategy"
        }
    };

    const handleOptionClick = async (optionKey) => {
        if (!isPro) {
            onUpgrade?.('continue');
            return;
        }

        if (!isAuthenticated) {
            toast.error('Please sign in to continue building');
            return;
        }

        setSelectedOption(optionKey);
        setIsLoading(true);
        setLoadingMessage('Generating with AI...');

        try {
            const headers = getAuthHeaders();
            const requestPayload = {
                blueprint_id: blueprintId,
                option: optionKey,
                original_idea: originalIdea || '',
                blueprint_summary: blueprintSummary || '',
                session_id: localStorage.getItem('vcc_session_id'),
                project_id: projectId || null  // Auto-save to project if provided
            };
            
            // Set up a timer to update message if taking too long
            const slowTimer = setTimeout(() => {
                setLoadingMessage('AI is busy — trying backup model...');
            }, 15000);
            
            const response = await axios.post(
                `${BACKEND_URL}/api/continue-building`,
                requestPayload,
                { 
                    headers,
                    timeout: 50000  // 50 second timeout
                }
            );
            
            clearTimeout(slowTimer);

            if (response.data.success && response.data.content) {
                setResult(response.data.content);
                setShowResult(true);
                toast.success(`${OPTIONS[optionKey].name} generated!`);
                
                // Notify parent of new content (for project refresh)
                if (onContentGenerated && response.data.saved_to_project) {
                    onContentGenerated(optionKey, response.data.content);
                }
            } else {
                const errorMsg = response.data.error || 'Generation failed. Please try again.';
                toast.error(errorMsg);
            }
        } catch (error) {
            console.error('Continue building failed:', error);
            if (error.response?.status === 403) {
                toast.error('Upgrade to Pro to unlock Continue Building');
                onUpgrade?.('continue');
            } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                toast.error('Request timed out. AI service may be busy — please try again.');
            } else {
                toast.error('Temporarily unavailable. Please try again in a moment.');
            }
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
        }
    };

    const closeResult = () => {
        setShowResult(false);
        setResult(null);
        setSelectedOption(null);
    };

    return (
        <>
            <div className="mt-8 p-6 rounded-xl bg-zinc-900/50 border border-zinc-800">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="font-semibold text-white flex items-center gap-2">
                            Continue Building
                            {!isPro && (
                                <span className="px-2 py-0.5 text-xs bg-violet-500/20 text-violet-300 rounded-full flex items-center gap-1">
                                    <Crown className="w-3 h-3" />
                                    Pro
                                </span>
                            )}
                        </h3>
                        <p className="text-sm text-zinc-400">
                            Build your app step-by-step
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {Object.entries(OPTIONS).map(([key, option]) => {
                        const Icon = OPTION_ICONS[key];
                        const colorClass = OPTION_COLORS[key];
                        const isSelected = selectedOption === key && isLoading;

                        return (
                            <button
                                key={key}
                                onClick={() => handleOptionClick(key)}
                                disabled={isLoading}
                                className={`relative p-4 rounded-xl border transition-all text-left group ${
                                    isPro
                                        ? 'border-zinc-700 hover:border-violet-500/50 hover:bg-zinc-800/50'
                                        : 'border-zinc-800 opacity-60 cursor-not-allowed'
                                } ${isSelected ? 'border-violet-500 bg-zinc-800/50' : ''}`}
                                data-testid={`continue-${key}`}
                            >
                                {!isPro && (
                                    <div className="absolute top-2 right-2">
                                        <Lock className="w-3 h-3 text-zinc-600" />
                                    </div>
                                )}
                                
                                <div className={`w-10 h-10 rounded-lg ${colorClass} flex items-center justify-center mb-3`}>
                                    {isSelected ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <Icon className="w-5 h-5" />
                                    )}
                                </div>
                                
                                <p className="font-medium text-sm text-white mb-1">
                                    {option.name}
                                </p>
                                <p className="text-xs text-zinc-500">
                                    {option.description}
                                </p>

                                {isPro && (
                                    <ChevronRight className="absolute bottom-3 right-3 w-4 h-4 text-zinc-600 group-hover:text-violet-400 transition-colors" />
                                )}
                            </button>
                        );
                    })}
                </div>

                {!isPro && (
                    <div className="mt-4 p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
                        <p className="text-sm text-violet-300 flex items-center gap-2">
                            <Crown className="w-4 h-4" />
                            Unlock Continue Building with Pro - $11/month
                        </p>
                    </div>
                )}

                {/* Loading indicator with status message */}
                {isLoading && loadingMessage && (
                    <div className="mt-4 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                        <p className="text-sm text-zinc-300 flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
                            {loadingMessage}
                        </p>
                    </div>
                )}
            </div>

            {/* Result Modal */}
            <AnimatePresence>
                {showResult && result && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={closeResult}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                                <div className="flex items-center gap-3">
                                    {selectedOption && (
                                        <div className={`w-10 h-10 rounded-lg ${OPTION_COLORS[selectedOption]} flex items-center justify-center`}>
                                            {React.createElement(OPTION_ICONS[selectedOption], { className: 'w-5 h-5' })}
                                        </div>
                                    )}
                                    <div>
                                        <h3 className="font-semibold text-white">{result.title}</h3>
                                        <p className="text-sm text-zinc-400">{OPTIONS[selectedOption]?.name}</p>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={closeResult}
                                >
                                    <X className="w-5 h-5" />
                                </Button>
                            </div>

                            {/* Content */}
                            <div className="p-6 overflow-y-auto max-h-[60vh]">
                                {/* Summary */}
                                <p className="text-zinc-300 mb-6">{result.summary}</p>

                                {/* Details */}
                                {result.details && result.details.length > 0 && (
                                    <div className="space-y-4 mb-6">
                                        {result.details.map((section, idx) => (
                                            <div key={idx} className="p-4 rounded-lg bg-zinc-800/50">
                                                <h4 className="font-medium text-white mb-2">{section.heading}</h4>
                                                <p className="text-sm text-zinc-400 whitespace-pre-wrap">{section.content}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Recommendations */}
                                {result.recommendations && result.recommendations.length > 0 && (
                                    <div className="mb-6">
                                        <h4 className="font-medium text-white mb-3">Recommendations</h4>
                                        <div className="space-y-2">
                                            {result.recommendations.map((rec, idx) => (
                                                <div key={idx} className="flex items-start gap-2 text-sm">
                                                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                                                    <span className="text-zinc-300">{rec}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Next Steps */}
                                {result.next_steps && result.next_steps.length > 0 && (
                                    <div>
                                        <h4 className="font-medium text-white mb-3">Next Steps</h4>
                                        <div className="space-y-2">
                                            {result.next_steps.map((step, idx) => (
                                                <div key={idx} className="flex items-start gap-2 text-sm">
                                                    <span className="w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center text-xs shrink-0">
                                                        {idx + 1}
                                                    </span>
                                                    <span className="text-zinc-300">{step}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
